import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface SLNavbarProps {
  ctaLabel: string;
  onCtaClick: () => void;
}

export default function SLNavbar({ ctaLabel, onCtaClick }: SLNavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-zinc-950/95 backdrop-blur-md border-b border-zinc-800/60 py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          className="flex items-center gap-2 cursor-pointer"
        >
          <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-lg overflow-hidden">
            <i className="ri-rocket-2-fill text-white text-sm" />
          </div>
          <span className="text-white font-bold text-xl tracking-tight">
            boost<span className="text-orange-500">io</span>
          </span>
        </a>

        {/* Desktop nav links */}
        <nav className="hidden md:flex items-center gap-8">
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            className="text-zinc-400 hover:text-white text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Hlavná stránka
          </a>
          <a
            href="/#sluzby"
            onClick={(e) => { e.preventDefault(); navigate('/'); setTimeout(() => { const el = document.querySelector('#sluzby'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 200); }}
            className="text-zinc-400 hover:text-white text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Všetky služby
          </a>
          <a
            href="/#cennik"
            onClick={(e) => { e.preventDefault(); navigate('/'); setTimeout(() => { const el = document.querySelector('#cennik'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 200); }}
            className="text-zinc-400 hover:text-white text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Cenník
          </a>
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={onCtaClick}
            className="bg-orange-500 hover:bg-orange-400 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {ctaLabel}
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden w-9 h-9 flex items-center justify-center text-white cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menu"
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-zinc-950 border-t border-zinc-800 px-6 py-4 flex flex-col gap-4">
          <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); setMobileOpen(false); }} className="text-zinc-300 hover:text-white text-base font-medium py-1 cursor-pointer">
            Hlavná stránka
          </a>
          <a href="/#sluzby" onClick={(e) => { e.preventDefault(); navigate('/'); setMobileOpen(false); }} className="text-zinc-300 hover:text-white text-base font-medium py-1 cursor-pointer">
            Všetky služby
          </a>
          <button
            onClick={() => { setMobileOpen(false); onCtaClick(); }}
            className="bg-orange-500 text-white text-sm font-semibold px-5 py-3 rounded-lg text-center cursor-pointer whitespace-nowrap"
          >
            {ctaLabel}
          </button>
        </div>
      )}
    </header>
  );
}
